import { useFormikContext } from 'formik'
import { filter, findIndex, path, propEq, reject, update } from 'ramda'
import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import styled from 'styled-components'
import { Button } from '@/components/button'
import Icon from '@/components/icon'
import useContractActiveAddendumTemplates from '@/hooks/use-contract-active-addendum-templates'
import { colors, fontWeights } from '@/styles'
import AddendumList from './addendum-list'
import { show as showAddendumModal } from './addendum-modal'
import formatAddendum from './format-addendum'
import getVariables from './get-variables'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;

  ${props =>
    props.hasAddendum &&
    `
    justify-content: flex-start;
  `}
`

const NoAddendumsText = styled.p`
  color: ${colors.regular};
  font-size: 23px;
  font-weight: ${fontWeights.bold};
  letter-spacing: -0.4px;
  line-height: 28px;
  text-align: center;
  margin: 20px 0 30px;
`

const ContractAddendums = ({ listing }) => {
  const { t } = useTranslation()
  const addendumTemplates = useContractActiveAddendumTemplates()
  const { setFieldValue, values: data } = useFormikContext()
  const { contract_addendums = [] } = data
  const userRole = useSelector(path(['session', 'session', 'user_role']))
  const isAdmin = userRole === 'administrator'
  const planType = useSelector(path(['session', 'session', 'company_info', 'account_type']))
  const isOntario = listing?.building?.state === 'ON'
  const isAlberta = listing?.building?.state === 'AB'

  const variables = getVariables(listing, data)

  if (!addendumTemplates) return null

  const hasAddendum = contract_addendums.length > 0

  const standardTemplates = formatAddendum(
    filter(propEq('addendum_type', 'standard'), addendumTemplates)
  )
  const customTemplates = formatAddendum(
    filter(propEq('addendum_type', 'company'), addendumTemplates)
  )

  const addContractAddendums = (addendums = []) => {
    setFieldValue('contract_addendums', [...contract_addendums, ...addendums])
  }

  const removeContractAddendum = addendum => {
    const withoutOriginal = reject(propEq('title', addendum.title), contract_addendums)
    setFieldValue('contract_addendums', withoutOriginal)
  }

  const updateContractAddendum = (updatedAddendum, addendum) => {
    const updatedTemplates = update(
      findIndex(propEq('title', addendum.title), contract_addendums),
      updatedAddendum,
      contract_addendums
    )
    setFieldValue('contract_addendums', updatedTemplates)
  }

  const handleShowAddendumsModal = view => {
    showAddendumModal({
      initialView: view,
      addContractAddendums,
      standardTemplates,
      customTemplates,
      isAdmin,
      variables,
      planType: planType,
      isOntario: isOntario,
      isAlberta: isAlberta
    })
  }

  return (
    <Wrapper hasAddendum={hasAddendum}>
      {hasAddendum ? (
        <AddendumList
          contractAddendums={contract_addendums}
          handleShowAddendumsModal={handleShowAddendumsModal}
          removeContractAddendum={removeContractAddendum}
          updateContractAddendum={updateContractAddendum}
          variables={variables}
        />
      ) : (
        <>
          <Icon id="addendum" width={103} height={124} />
          <NoAddendumsText>
            {t('l.contract.edit.addendums_no_addendum.subtitle', 'No addendums selected')}
          </NoAddendumsText>
          <Button onClick={() => handleShowAddendumsModal()}>
            {t('l.contract.edit.addendums_add_addendum.button', 'Add Addendums')}
          </Button>
        </>
      )}
    </Wrapper>
  )
}
export default memo(ContractAddendums)
